import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import axios from "axios";
import { isNonProductionEnvironment } from "library/utils";

export function InvoicePayment() {
  const { offerId, invoiceId } = useParams<{
    offerId: string;
    invoiceId: string;
  }>();
  const [error, setError] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [isAlreadyPaid, setIsAlreadyPaid] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState("");
  // Credit card payment (EveryPay) is offered outside production only until it is released
  const showCardPayment = isNonProductionEnvironment();

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!offerId || !invoiceId) {
        setIsChecking(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_PUB_URL}/invoicepayment/check/${offerId}/${invoiceId}`
        );

        // 200 means not paid, display payment options
        if (response.status === 200) {
          setIsAlreadyPaid(false);
        }
      } catch (error: any) {
        // 400 means already paid
        if (error.response?.status === 400) {
          setIsAlreadyPaid(true);
        } else {
          console.error("Error checking payment status:", error);
          setError(
            "Neizdevās pārbaudīt maksājuma statusu. Lūdzu, mēģiniet vēlreiz."
          );
        }
      } finally {
        setIsChecking(false);
      }
    };

    checkPaymentStatus();
  }, [offerId, invoiceId]);

  const handleCardPayment = async () => {
    setError(null);
    setIsProcessingPayment(true);
    setPaymentMessage("Notiek pārvirzīšana uz maksājumu sistēmu...");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_PUB_URL}/invoicepayment/pay/creditcard/${offerId}/${invoiceId}`
      );
      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error("Error initiating card payment:", error);
      setError(
        "Neizdevās inicializēt maksājumu ar kredītkarti. Lūdzu, mēģiniet vēlreiz."
      );
    } finally {
      setTimeout(() => setIsProcessingPayment(false), 1000);
    }
  };

  const handleBankPayment = async (bankName: string) => {
    setError(null);
    setIsProcessingPayment(true);
    setPaymentMessage("Notiek pārvirzīšana uz internetbanku...");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_PUB_URL}/invoicepayment/pay/bank/${bankName}/${offerId}/${invoiceId}`
      );
      if (response.data) {
        // SwedbankLV returns a URL directly, similar to credit card payment
        if (bankName === "SwedbankLV") {
          window.location.href = response.data;
        } else {
          // Other banks return an HTML form
          // Create a temporary container to parse the HTML form
          const tempDiv = document.createElement("div");
          tempDiv.innerHTML = response.data;

          // Find the form in the response
          const form = tempDiv.querySelector("form");
          if (form) {
            // Append the form to the document body
            document.body.appendChild(form);
            // Submit the form
            form.submit();
          } else {
            throw new Error("No form found in response");
          }
        }
      }
    } catch (error) {
      console.error("Error initiating bank payment:", error);
      setError(
        "Neizdevās inicializēt maksājumu ar internetbanku. Lūdzu, mēģiniet vēlreiz."
      );
    } finally {
      setTimeout(() => setIsProcessingPayment(false), 1000);
    }
  };

  if (isChecking) {
    return (
      <Container>
        <Logo src="/greco.svg" alt="GrECo" />
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Pārbauda maksājuma statusu...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  if (isAlreadyPaid) {
    return (
      <Container>
        <Logo src="/greco.svg" alt="GrECo" />
        <Content>
          <MessageContainer>
            <MessageTitle>Rēķins jau ir apmaksāts</MessageTitle>
            <MessageText>
              Šis rēķins jau ir apmaksāts. Ja Jums ir jautājumi, lūdzu,
              sazinieties ar GrECo Latvia, SIA.
            </MessageText>
          </MessageContainer>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Logo src="/greco.svg" alt="GrECo" />
      {isProcessingPayment && (
        <PaymentBackdrop>
          <PaymentSpinnerContainer>
            <PaymentSpinner />
            <PaymentMessage>{paymentMessage}</PaymentMessage>
          </PaymentSpinnerContainer>
        </PaymentBackdrop>
      )}
      {error && (
        <ErrorBanner>
          <ErrorIcon>●</ErrorIcon>
          <ErrorText>{error}</ErrorText>
        </ErrorBanner>
      )}
      <Content $hasError={!!error}>
        <PaymentTypes>
          <Column>
            <SectionTitle>Apmaksāt izmantojot internetbanku</SectionTitle>
            <BankList>
              <BankItem>
                <BankButton
                  className="swedbank"
                  onClick={() => handleBankPayment("SwedbankLV")}
                >
                  <span>Swedbank</span>
                </BankButton>
              </BankItem>
              <BankItem>
                <BankButton
                  className="luminor"
                  onClick={() => handleBankPayment("DnbLV")}
                >
                  <span>Luminor</span>
                </BankButton>
              </BankItem>
              <BankItem>
                <BankButton
                  className="seb"
                  onClick={() => handleBankPayment("SEBLV")}
                >
                  <span>SEB</span>
                </BankButton>
              </BankItem>
            </BankList>
          </Column>

          {showCardPayment && (
          <Column>
            <SectionTitle>Apmaksāt izmantojot kredītkarti</SectionTitle>
            <CardList>
              <CardItem>
                <CardButton className="mastercard" onClick={handleCardPayment}>
                  <span>MasterCard</span>
                </CardButton>
              </CardItem>
              <CardItem>
                <CardButton className="maestro" onClick={handleCardPayment}>
                  <span>Maestro</span>
                </CardButton>
              </CardItem>
              <CardItem>
                <CardButton
                  className="visa-electron"
                  onClick={handleCardPayment}
                >
                  <span>Visa Electron</span>
                </CardButton>
              </CardItem>
              <CardItem>
                <CardButton className="visa" onClick={handleCardPayment}>
                  <span>Visa</span>
                </CardButton>
              </CardItem>
            </CardList>
          </Column>
          )}
        </PaymentTypes>

        <InfoSection>
          <InfoText>
            <p>
              Informējam, ka maksājumu apstrādi veic Jūsu izvēlētā banka.
              Izvēloties apmaksas veidu, Jūs nokļūsiet bankas vai maksājumu
              kartes autorizācijas lapā, par kuras drošību atbild attiecīgā
              finanšu iestāde – GrECo Latvia, SIA šajā gadījumā ir starpnieks,
              kam nav pieejami maksājumu autorizācijas dati. GrECo Latvia, SIA
              no attiecīgās finanšu iestādes saņem tikai informāciju par
              maksājuma faktu – apstiprinājumu vai atteikumu.
            </p>
            <p>
              Maksājuma process var ilgt no dažām sekundēm, līdz vairākām
              minūtēm.{" "}
              <strong>
                NEKĀDĀ GADĪJUMĀ PROCESA LAIKĀ NESPIEDIET POGU BACK (ATPAKAĻ)!!!
              </strong>
            </p>
            <p>
              Pēc maksājuma procesa beigām Jums uz ekrāna parādīsies paziņojums
              par maksājuma apstiprinājumu vai atteikumu.{" "}
              <strong>
                MAKSĀJUMA APSTIPRINĀJUMA LOGU NEKĀDĀ GADĪJUMĀ NEVERIET CIET
              </strong>
              , bet turpiniet darbības, lai nokļūtu atpakaļ pie pakalpojuma
              sniedzēja – aizverot maksājuma apstiprinājuma logu, Jūs
              pārtrauksiet procesu, kā rezultātā darījums nenotiks, neskatoties
              uz to, ka naudu būsiet samaksājuši.
            </p>
            <p>
              Nenotikušas apmaksas gadījumā, kam par iemeslu var būt bankas
              atteikums, interneta sakaru traucējumi, pārtraukts maksājuma
              process un citas tehniskas problēmas, darījums tiek uzskatīts par
              nenotikušu.
            </p>
            <p>
              Jebkuru jautājumu gadījumā lūgums sazināties ar{" "}
              <strong>GrECo Latvia, SIA</strong>
            </p>
          </InfoText>
        </InfoSection>
      </Content>
    </Container>
  );
}

const Container = styled.div`
  min-height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  background-color: #f5f5f5;
  margin: 0;
  padding: 40px 20px;
  box-sizing: border-box;
  position: relative;
`;

const Logo = styled.img`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 108px;
  height: 108px;
`;

const ErrorBanner = styled.div`
  max-width: 1000px;
  width: 100%;
  margin-top: 140px;
  margin-bottom: 20px;
  padding: 16px 20px;
  background-color: #fde7e9;
  border: 1px solid #e74c3c;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ErrorIcon = styled.span`
  color: #e74c3c;
  font-size: 20px;
  line-height: 1;
`;

const ErrorText = styled.span`
  color: #c0392b;
  font-size: 14px;
  line-height: 1.5;
  flex: 1;
`;

const Content = styled.div<{ $hasError?: boolean }>`
  background-color: #ffffff;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  padding: 30px;
  max-width: 1000px;
  width: 100%;
  margin-top: ${(props) => (props.$hasError ? "0" : "140px")};
`;

const PaymentTypes = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 20px;
  background-color: #fafafa;
`;

const SectionTitle = styled.h2`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #5b6a99;
  text-align: left;
`;

const BankList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const BankItem = styled.li`
  flex: 0 0 auto;
`;

const BankButton = styled.button`
  width: 120px;
  height: 58px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  background-image: url("/octa-sprite.png");
  background-repeat: no-repeat;
  background-size: contain;
  overflow: hidden;
  padding: 0;
  display: inline-block;
  vertical-align: top;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border-color: #999;
  }

  span {
    display: none;
  }

  &.swedbank {
    background-position: center -55px;
    background-size: 75px auto;
  }

  &.luminor {
    background-position: center -171px;
    background-size: 75px auto;
  }

  &.seb {
    background-position: center -113px;
    background-size: 75px auto;
  }
`;

// Card styles of the credit card payment section (shown outside production)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CardList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(4, 110px);
  gap: 10px;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CardItem = styled.li`
  flex: 0 0 auto;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CardButton = styled.button`
  width: 110px;
  height: 58px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  background-image: url("/octa-sprite.png");
  background-repeat: no-repeat;
  background-size: 75px auto;
  overflow: hidden;
  padding: 0;
  display: inline-block;
  vertical-align: top;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border-color: #999;
  }

  span {
    display: none;
  }

  &.mastercard {
    background-position: center -287px;
  }

  &.maestro {
    background-position: center -345px;
  }

  &.visa-electron {
    background-position: center -403px;
  }

  &.visa {
    background-position: center -461px;
  }
`;

const InfoSection = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 20px;
  background-color: #fafafa;
`;

const InfoText = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: #5b6a99;

  p {
    margin: 0 0 12px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    color: #d32f2f;
    font-weight: 700;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  margin-top: 140px;
  gap: 16px;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e0e0e0;
  border-top-color: #5b6a99;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-size: 14px;
  color: #5b6a99;
`;

const MessageContainer = styled.div`
  padding: 40px;
  text-align: center;
`;

const MessageTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #5b6a99;
  margin: 0 0 16px 0;
`;

const MessageText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #5b6a99;
  margin: 0;
`;

const PaymentBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const PaymentSpinnerContainer = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 40px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const PaymentSpinner = styled.div`
  width: 56px;
  height: 56px;
  border: 5px solid #e0e0e0;
  border-top-color: #5b6a99;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const PaymentMessage = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #5b6a99;
  text-align: center;
  max-width: 300px;
`;
